// src/pages/sale/sale.styled.ts

import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-6px) scaleY(0.98); }
  to { opacity: 1; transform: translateY(0) scaleY(1); }
`;

const pop = keyframes`
  0% { opacity: 0; transform: scale(0.94); }
  60% { opacity: 1; transform: scale(1.015); }
  100% { opacity: 1; transform: scale(1); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/* ===================== Layout geral ===================== */

export const SaleContainer = styled.div`
  display: grid;
  grid-template-columns: 1.55fr 1fr;
  gap: 20px;
  height: calc(100vh - 108px);
  min-height: 580px;
  color: var(--text);
  animation: ${fadeUp} 0.35s ease both;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

export const LeftPanel = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 24px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 20px;
  box-shadow: var(--shadow);
`;

export const RightPanel = styled.aside`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 20px;
  box-shadow: var(--shadow);
  overflow: hidden;

  @media (max-width: 980px) {
    min-height: 420px;
  }
`;

/* ===================== Cabeçalho do painel esquerdo ===================== */

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

export const Eyebrow = styled.span`
  display: block;
  font-family: var(--font-display);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 2px;
`;

export const PanelTitle = styled.h2`
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.3rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--text);
`;

export const ModeSwitch = styled.div`
  display: inline-flex;
  padding: 4px;
  gap: 4px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface-2);
  flex-shrink: 0;
`;

export const ModeButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  border-radius: 9px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  color: ${({ $active }) => ($active ? "#fff" : "var(--text-muted)")};
  background: ${({ $active }) =>
    $active ? "var(--gradient-brand-soft)" : "transparent"};
  box-shadow: ${({ $active }) =>
    $active ? "0 6px 16px rgba(43,123,255,.28)" : "none"};
  transition: all 0.18s ease;

  &:hover {
    color: ${({ $active }) => ($active ? "#fff" : "var(--accent)")};
  }
`;

export const IconGhostButton = styled.button`
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border: 1px solid var(--border);
  border-radius: 13px;
  background: var(--surface);
  color: var(--text-muted);
  cursor: pointer;
  font-size: 17px;
  transition: all 0.18s ease;
  flex-shrink: 0;

  &:hover {
    color: #fff;
    background: var(--gradient-brand-soft);
    border-color: transparent;
    box-shadow: var(--glow);
    transform: translateY(-1px);
  }
`;

/* ===================== Busca ===================== */

export const SearchWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SearchField = styled.div<{ $scanning?: boolean }>`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  height: 58px;
  background: var(--surface-2);
  border: 1.5px solid
    ${({ $scanning }) => ($scanning ? "var(--accent)" : "var(--border)")};
  border-radius: 14px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  ${({ $scanning }) =>
    $scanning && `box-shadow: 0 0 0 4px rgba(43,123,255,.14);`}

  &:focus-within {
    border-color: var(--accent);
    box-shadow: 0 0 0 4px rgba(43, 123, 255, 0.14);
  }

  svg:first-child {
    color: var(--text-muted);
    font-size: 19px;
    flex-shrink: 0;
  }
`;

export const SearchInputEl = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text);
  font-family: var(--font-body);
  font-size: 16px;

  &::placeholder {
    color: var(--text-dim);
  }
`;

export const ClearQueryBtn = styled.button`
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 50%;
  background: var(--surface);
  color: var(--text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s ease;

  &:hover {
    color: #fb7185;
  }
`;

export const ScanBadge = styled.span`
  flex-shrink: 0;
  padding: 4px 9px;
  border-radius: 7px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: #fff;
  background: var(--gradient-brand-soft);
`;

export const ResultsDropdown = styled.div`
  position: absolute;
  z-index: 20;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  max-height: 420px;
  overflow-y: auto;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  animation: ${slideDown} 0.16s ease both;
  transform-origin: top center;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--scroll-thumb);
    border-radius: 8px;
  }
`;

export const ResultRow = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  cursor: pointer;
  border-left: 3px solid
    ${({ $active }) => ($active ? "var(--accent)" : "transparent")};
  background: ${({ $active }) =>
    $active ? "rgba(43,123,255,.09)" : "transparent"};
  transition: background 0.12s ease;

  & + & {
    border-top: 1px solid var(--border);
  }
  &:hover {
    background: rgba(43, 123, 255, 0.09);
  }
`;

export const ResultInfo = styled.div`
  flex: 1;
  min-width: 0;

  strong {
    display: block;
    font-size: 14.5px;
    font-weight: 700;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  span {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: var(--text-muted);
  }
`;

export const StockBadge = styled.span<{ $low?: boolean }>`
  flex-shrink: 0;
  padding: 3px 8px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  color: ${({ $low }) => ($low ? "#fb7185" : "#34d399")};
  background: ${({ $low }) =>
    $low ? "rgba(251,113,133,.12)" : "rgba(52,211,153,.12)"};
`;

export const QtyStepper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  button {
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    border: 1px solid var(--border);
    border-radius: 7px;
    background: var(--surface);
    color: var(--text);
    cursor: pointer;
    font-size: 13px;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
      border-color: var(--accent);
      color: var(--accent);
    }
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  span {
    min-width: 20px;
    text-align: center;
    font-size: 13.5px;
    font-weight: 700;
  }
`;

export const ResultPrice = styled.div`
  flex-shrink: 0;
  min-width: 78px;
  text-align: right;
  font-weight: 800;
  font-size: 14.5px;
  color: var(--accent);
`;

export const DropdownState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 26px 16px;
  color: var(--text-muted);
  font-size: 13.5px;
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 15px;
  height: 15px;
  border: 2px solid var(--border-strong);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

/* ===================== Corpo do painel esquerdo ===================== */

export const PanelBody = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-top: 22px;
`;

export const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
  color: var(--text-dim);
  padding: 40px 20px;

  .icon-wrap {
    width: 84px;
    height: 84px;
    display: grid;
    place-items: center;
    border-radius: 24px;
    background: var(--surface-2);
    border: 1.5px dashed var(--border-strong);
    color: var(--text-muted);
    font-size: 32px;
  }
  h3 {
    margin: 0;
    font-family: var(--font-display);
    font-size: 17px;
    font-weight: 700;
    color: var(--text);
  }
  p {
    margin: 0;
    font-size: 13.5px;
    max-width: 320px;
    line-height: 1.5;
  }

  .hint-row {
    display: flex;
    gap: 8px;
    margin-top: 6px;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }

  kbd {
    padding: 3px 7px;
    border-radius: 6px;
    background: var(--surface-2);
    border: 1px solid var(--border-strong);
    font-size: 11px;
    font-weight: 700;
    color: var(--text-muted);
  }
`;

export const LastAddedCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 18px;
  padding: 8px 4px;
  animation: ${pop} 0.32s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

export const LastAddedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 5px 12px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 800;
  color: #34d399;
  background: rgba(52, 211, 153, 0.12);
`;

export const LastAddedIcon = styled.div`
  width: 96px;
  height: 96px;
  display: grid;
  place-items: center;
  border-radius: 26px;
  background: var(--gradient-brand-soft);
  color: #fff;
  font-size: 38px;
  box-shadow: var(--glow);
`;

export const LastAddedName = styled.h2`
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(1.25rem, 2.3vw, 1.65rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--text);
  line-height: 1.28;
`;

export const LastAddedMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 13.5px;
  color: var(--text-muted);

  b {
    color: var(--accent);
    font-weight: 800;
  }
`;

/* ===================== Tipo de recebimento ===================== */

export const PaymentSection = styled.div`
  flex-shrink: 0;
  padding: 18px 22px 4px;
  border-top: 1px solid var(--border);
`;

export const PaymentSectionLabel = styled.span`
  display: block;
  margin-bottom: 10px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-dim);
`;

export const PaymentTypeRow = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  padding: 4px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface-2);
`;

export const PaymentTypeIndicator = styled.div`
  position: absolute;
  top: 4px;
  /* left fica em 0 de propósito — o GSAP usa pill.offsetLeft (mesma
     referência: o padding-edge do PaymentTypeRow, que é o offsetParent
     deste elemento) para posicionar via transform, sem duplicar o padding. */
  left: 0;
  height: calc(100% - 8px);
  width: 0;
  border-radius: 10px;
  background: var(--gradient-brand-soft);
  box-shadow: var(--glow);
  pointer-events: none;
  will-change: transform, width;
`;

export const PaymentTypePill = styled.button<{ $active?: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 9px 4px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: ${({ $active }) => ($active ? "#fff" : "var(--text-muted)")};
  font-size: 11.5px;
  font-weight: 800;
  cursor: pointer;
  transition: color 0.18s ease;

  svg {
    font-size: 15px;
  }

  &:hover {
    color: ${({ $active }) => ($active ? "#fff" : "var(--accent)")};
  }
`;

export const DeferredFieldsWrap = styled.div`
  overflow: hidden;
  height: 0;
  opacity: 0;
`;

export const DeferredFieldsInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 14px;
`;

export const DeferredHint = styled.p`
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(245, 158, 11, 0.1);
  border: 1px dashed rgba(245, 158, 11, 0.45);
  color: var(--text-muted);
  font-size: 11.5px;
  line-height: 1.5;
`;

/* ===================== Painel direito (carrinho) ===================== */

export const CartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 22px 22px 16px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
`;

export const CartTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  h2 {
    margin: 0;
    font-family: var(--font-display);
    font-size: 19px;
    font-weight: 800;
    color: var(--text);
  }
`;

export const CountBadge = styled.span`
  display: grid;
  place-items: center;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 100px;
  background: var(--gradient-brand-soft);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
`;

export const ClearCartBtn = styled.button`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover:not(:disabled) {
    color: #fb7185;
    border-color: #fb7185;
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

export const CartList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 6px 14px;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--scroll-thumb);
    border-radius: 8px;
  }
`;

export const CartRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 8px;
  border-bottom: 1px solid var(--border);
  animation: ${fadeUp} 0.22s ease both;

  &:last-child {
    border-bottom: none;
  }
`;

export const CartRowInfo = styled.div`
  flex: 1;
  min-width: 0;

  strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const CartRowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-muted);
`;

export const DiscountChip = styled.button<{ $hasDiscount?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 6px;
  border: 1px dashed
    ${({ $hasDiscount }) => ($hasDiscount ? "#fbbf24" : "var(--border-strong)")};
  background: ${({ $hasDiscount }) =>
    $hasDiscount ? "rgba(251,191,36,.12)" : "transparent"};
  color: ${({ $hasDiscount }) => ($hasDiscount ? "#fbbf24" : "var(--text-dim)")};
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
`;

export const DiscountInput = styled.input`
  width: 46px;
  padding: 2px 4px;
  border: 1px solid var(--border-strong);
  border-radius: 6px;
  background: var(--surface-2);
  color: var(--text);
  font-size: 11px;
  text-align: center;
`;

/* ===================== Popover de desconto ===================== */

export const DiscountPopoverPanel = styled.div`
  min-width: 236px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: var(--surface);
  color: var(--text);
`;

export const DiscountPopoverLabel = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);

  strong {
    display: block;
    margin-top: 3px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: normal;
    text-transform: none;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const DiscountPopoverInputRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  padding: 10px 0;
  border-radius: 12px;
  background: var(--surface-2);
  border: 1px solid var(--border);

  input {
    width: 70px;
    border: none;
    outline: none;
    background: transparent;
    color: var(--accent);
    font-family: var(--font-display);
    font-size: 1.8rem;
    font-weight: 800;
    text-align: right;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  span {
    font-size: 1.2rem;
    font-weight: 800;
    color: var(--accent);
  }
`;

export const PresetRow = styled.div`
  display: flex;
  gap: 6px;
`;

export const PresetChip = styled.button<{ $active?: boolean }>`
  flex: 1;
  padding: 8px 0;
  border-radius: 9px;
  border: 1px solid
    ${({ $active }) => ($active ? "var(--accent)" : "var(--border)")};
  background: ${({ $active }) =>
    $active ? "rgba(43,123,255,.14)" : "var(--surface-2)"};
  color: ${({ $active }) => ($active ? "var(--accent)" : "var(--text-muted)")};
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

export const PopoverActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const RemoveDiscountLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: var(--text-dim);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  visibility: ${({ disabled }) => (disabled ? "hidden" : "visible")};

  &:hover {
    color: #fb7185;
  }
`;

export const ApplyDiscountBtn = styled.button`
  padding: 9px 18px;
  border: none;
  border-radius: 10px;
  background: var(--gradient-brand-soft);
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: var(--glow);
  transition: all 0.15s ease;

  &:hover {
    filter: brightness(1.06);
  }
`;

export const CartQtyStepper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  button {
    display: grid;
    place-items: center;
    width: 26px;
    height: 26px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface-2);
    color: var(--text);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
      border-color: var(--accent);
      color: var(--accent);
    }
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  input {
    width: 32px;
    text-align: center;
    border: none;
    background: transparent;
    color: var(--text);
    font-weight: 700;
    font-size: 14px;
  }
`;

export const CartRowTotal = styled.div`
  flex-shrink: 0;
  min-width: 76px;
  text-align: right;
  font-weight: 800;
  font-size: 14.5px;
  color: var(--text);
`;

export const CartRowRemove = styled.button`
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text-dim);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #fb7185;
    background: rgba(251, 113, 133, 0.1);
  }
`;

export const CartEmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  color: var(--text-dim);
  padding: 30px;

  .icon-wrap {
    width: 64px;
    height: 64px;
    display: grid;
    place-items: center;
    border-radius: 20px;
    background: var(--surface-2);
    font-size: 26px;
    color: var(--text-muted);
  }
  h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: var(--text-muted);
  }
  p {
    margin: 0;
    font-size: 12.5px;
    max-width: 220px;
  }
`;

export const CartFooter = styled.div`
  flex-shrink: 0;
  padding: 18px 22px 22px;
  border-top: 1px solid var(--border);
  background: var(--surface-2);
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: var(--text-muted);
  margin-bottom: 4px;

  b {
    color: #fb7185;
    font-weight: 700;
  }
`;

export const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 10px 0 16px;

  span:first-child {
    font-size: 12.5px;
    font-weight: 700;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
`;

export const TotalValue = styled.span`
  font-family: var(--font-display);
  font-size: 1.85rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  background: var(--gradient-brand);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const ActionsRow = styled.div`
  display: flex;
  gap: 10px;
`;

export const CancelSaleBtn = styled.button`
  flex: 0 0 auto;
  padding: 0 18px;
  height: 50px;
  border-radius: 12px;
  border: 1.5px solid var(--border-strong);
  background: transparent;
  color: var(--text-muted);
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover:not(:disabled) {
    border-color: #fb7185;
    color: #fb7185;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const FinalizeBtn = styled.button`
  flex: 1;
  height: 50px;
  border-radius: 12px;
  border: none;
  background: var(--gradient-brand-soft);
  color: #fff;
  font-weight: 800;
  font-size: 15.5px;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: var(--glow);
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    filter: brightness(1.06);
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;
